// Reader-engagement beacons (save/unsave/share click/read end) and media-error reports.
// Fire-and-forget: a failed beacon never blocks the visitor's action. Every event carries a stable op_id so a
// retried request is counted once on the server.
import { API_BASE } from "@/lib/api";
import { whenQuiet } from "@/lib/netGate";

const beacon = (url, body) => whenQuiet(() => {
    try {
        if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: "application/json" }))) return;
    } catch (_) { /* fall through */ }
    fetch(url, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body, keepalive: true, priority: "low" }).catch(() => {});
});

export const newOpId = () => {
    try { return crypto.randomUUID().replace(/-/g, ""); } catch { return Math.random().toString(36).slice(2) + Date.now().toString(36); }
};

const currentTheme = () => (document.documentElement.classList.contains("dark") ? "dark" : "light");

export function sendReaderEvent(kind, { articleId, slug, lang, target, opId, value, metrics }) {
    if (!articleId && !["lang_change", "theme_change"].includes(kind)) return;
    beacon(`${API_BASE}/public/analytics/reader-event`, JSON.stringify({
        op_id: opId || newOpId(), kind, article_id: articleId || "-", slug: slug || null, lang: lang || null, theme: currentTheme(), target: target || null,
        value: value ?? null, metrics: metrics || null,
    }));
}

/** «تقدّم التمرير»: max % of the article body scrolled past the viewport bottom — sent when it grows by ≥10 points, and on leave. */
export function trackScrollProgress(el, { articleId, slug }) {
    if (!el || !articleId) return () => {};
    let max = 0, sent = 0, raf = 0;
    const measure = () => {
        raf = 0;
        const r = el.getBoundingClientRect(); const h = r.height || 1;
        const pct = Math.max(0, Math.min(100, Math.round(((window.innerHeight - r.top) / h) * 100)));
        if (pct > max) max = pct;
        if (max - sent >= 10) { sent = max; sendReaderEvent("scroll_progress", { articleId, slug, value: max }); }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(measure); };
    const flush = () => { if (max > sent) { sent = max; sendReaderEvent("scroll_progress", { articleId, slug, value: max }); } };
    window.addEventListener("scroll", onScroll, { passive: true }); window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", flush);
    measure();
    return () => { flush(); window.removeEventListener("scroll", onScroll); window.removeEventListener("pagehide", flush); document.removeEventListener("visibilitychange", flush); if (raf) cancelAnimationFrame(raf); };
}

// ---- media errors: ≤ 5 reports per browser session, identical (slug+path+kind) reported once per page load ----
const MEDIA_CAP = 5;
const seen = new Set();
const capKey = "media_err_sent";

export function classifyMediaError(el, kind) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return "network_offline";
    const code = el?.error?.code;
    if (kind === "video" && code === 2) return "network_offline";     // MEDIA_ERR_NETWORK
    if (kind === "video" && code === 3) return "decode_error";        // MEDIA_ERR_DECODE
    if (kind === "video" && code === 4) return "unsupported";         // MEDIA_ERR_SRC_NOT_SUPPORTED
    return "load_failed";
}

export function reportMediaError({ slug, url, kind, errorKind, detail }) {
    if (!slug || !url) return false;
    const path = String(url).split("?")[0].split("#")[0];
    const k = `${slug}|${path}|${errorKind}`;
    if (seen.has(k)) return false;
    let sent = 0;
    try { sent = parseInt(sessionStorage.getItem(capKey) || "0", 10) || 0; } catch { /* noop */ }
    if (sent >= MEDIA_CAP) return false;
    seen.add(k);
    try { sessionStorage.setItem(capKey, String(sent + 1)); } catch { /* noop */ }
    beacon(`${API_BASE}/public/media-error`, JSON.stringify({ article_slug: slug, media_url: path.slice(0, 1000), media_kind: kind, error_kind: errorKind, detail: (detail || "").slice(0, 120) }));
    return true;
}
